import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Trash2, Plus, BarChart3, Upload, FileText, AlertTriangle, Paperclip, ExternalLink, CheckCircle, XCircle, Clock, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PAYMENT_LABELS = { monthly: 'Monthly', upfront: 'Upfront', upon_request: 'Upon Request' };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatEUR(amount) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

export default function RubriqueDetailPage() {
  const { rubriqueId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [uploading, setUploading] = useState(null); // item_id being uploaded to

  // Add cost item form
  const [newDesc, setNewDesc] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newDate, setNewDate] = useState(new Date().toISOString().slice(0, 10));
  const [newRecurring, setNewRecurring] = useState(false);
  const [newRecurringAmount, setNewRecurringAmount] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [includeVat, setIncludeVat] = useState(false);
  const [vatRate, setVatRate] = useState('19');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getBudgetRubriqueDetail(rubriqueId);
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [rubriqueId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newAmount || parseFloat(newAmount) <= 0) { toast.error('Enter an amount'); return; }
    setSaving(true);
    try {
      const enteredAmount = parseFloat(newAmount);
      const rate = parseFloat(vatRate || '19') / 100;
      // When VAT is toggled, user enters GROSS — we back-calculate net
      const netAmount = includeVat ? Math.round(enteredAmount / (1 + rate) * 100) / 100 : enteredAmount;
      const vatAmount = includeVat ? Math.round((enteredAmount - netAmount) * 100) / 100 : 0;

      await api.addCostItem({
        rubrique_id: rubriqueId,
        description: newDesc,
        amount: netAmount,
        date: newDate,
        recurring: newRecurring,
        recurring_amount: newRecurring ? parseFloat(newRecurringAmount || '0') : 0,
        notes: newNotes,
        include_vat: includeVat,
        vat_rate: includeVat ? parseFloat(vatRate || '19') : 0,
      });
      toast.success(includeVat
        ? `Added: ${formatEUR(netAmount)} net + ${formatEUR(vatAmount)} VAT (from ${formatEUR(enteredAmount)} gross)`
        : 'Cost item added');
      setAddOpen(false);
      setNewDesc(''); setNewAmount(''); setNewDate(new Date().toISOString().slice(0, 10));
      setNewRecurring(false); setNewRecurringAmount(''); setNewNotes('');
      setIncludeVat(false); setVatRate('19');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleDelete = async (itemId) => {
    try {
      await api.deleteCostItem(itemId);
      toast.success('Deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDocDelete = async (item, doc) => {
    if (!window.confirm(`Beleg „${doc.filename}“ von „${item.description || 'Posten'}“ löschen?`)) return;
    try {
      await api.deleteCostItemDoc(item.id, doc.id);
      toast.success('Beleg gelöscht');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  const handleApprove = async (itemId) => {
    try {
      await api.approveCostItem(itemId);
      toast.success('Expense approved');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
  };

  const handleReject = async (itemId) => {
    try {
      await api.rejectCostItem(itemId);
      toast.success('Expense rejected');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
  };

  const handleDocUpload = async (itemId, file) => {
    setUploading(itemId);
    try {
      const CHUNK_SIZE = 512 * 1024;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      const initRes = await api.initCostItemDocUpload(itemId, file.name, totalChunks);
      const uploadId = initRes.data.upload_id;
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        await api.uploadCostItemDocChunk(itemId, uploadId, i, chunk);
      }
      await api.completeCostItemDocUpload(itemId, uploadId, file.name);
      toast.success('Document attached');
      load();
    } catch (err) { toast.error('Upload failed'); }
    setUploading(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { rubrique, plan, cost_items, monthly_actuals, total_actual, budget_24m, monthly_budget, cumulative_budget_to_date, cumulative_variance, is_over_pace, elapsed_months } = data;
  const utilization = budget_24m > 0 ? Math.round(total_actual / budget_24m * 100 * 10) / 10 : 0;
  const isVatRubrique = rubriqueId === 'olta_fee_vat';

  const months = Object.keys(monthly_actuals).sort();
  const maxMonthly = Math.max(...Object.values(monthly_actuals), monthly_budget, 1);

  return (
    <div className="max-w-5xl space-y-6" data-testid="rubrique-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/budget')} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl text-foreground tracking-tight">{rubrique.name}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{PAYMENT_LABELS[rubrique.payment]} &middot; Year 1: {formatEUR(rubrique.year1)} &middot; Year 2: {formatEUR(rubrique.year2)}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-cost-item-btn"
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4">
          <Plus className="w-4 h-4 mr-2" /> Add Cost Item
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">24m Budget</p>
          <p className="text-lg font-mono font-semibold text-foreground mt-1">{formatEUR(budget_24m)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{formatEUR(monthly_budget)}/month</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Spent</p>
          <p className="text-lg font-mono font-semibold text-foreground mt-1">{formatEUR(total_actual)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{cost_items.length} items</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Pace Budget ({elapsed_months}m)</p>
          <p className="text-lg font-mono font-semibold text-foreground mt-1">{formatEUR(cumulative_budget_to_date)}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{elapsed_months}/24 &times; {formatEUR(budget_24m)}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Pace Variance</p>
          <div className="flex items-center gap-1.5 mt-1">
            {is_over_pace && <AlertTriangle className="w-4 h-4 text-red-400" />}
            <p className={`text-lg font-mono font-semibold ${is_over_pace ? 'text-red-400' : 'text-emerald-400'}`}>
              {cumulative_variance >= 0 ? '+' : ''}{formatEUR(cumulative_variance)}
            </p>
          </div>
          <p className="text-[10px] text-muted-foreground mt-0.5">{is_over_pace ? 'Over pace' : 'Under pace'}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Utilization</p>
          {(() => {
            let textColor = 'text-emerald-400';
            let barColor = 'bg-emerald-500';
            if (utilization > 100) { textColor = 'text-red-400'; barColor = 'bg-red-500'; }
            else if (utilization > 80) { textColor = 'text-amber-400'; barColor = 'bg-amber-500'; }
            return (
              <>
                <p className={`text-lg font-mono font-semibold mt-1 ${textColor}`}>{utilization}%</p>
                <div className="w-full bg-secondary rounded-full h-1.5 mt-2">
                  <div className={`h-1.5 rounded-full ${barColor}`}
                    style={{ width: `${Math.min(utilization, 100)}%` }} />
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Monthly Chart with pace line */}
      {months.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-foreground/60" />
            <p className="text-sm font-heading text-foreground">Monthly Spending vs. Monthly Budget ({formatEUR(monthly_budget)}/mo)</p>
          </div>
          <div className="flex items-end gap-1 h-28">
            {months.map(m => {
              const val = monthly_actuals[m];
              const pct = (val / maxMonthly) * 100;
              const isOverMonth = val > monthly_budget;
              return (
                <div key={m} className="flex-1 flex flex-col items-center gap-1 relative">
                  <div className={`w-full rounded-t-sm transition-all ${isOverMonth ? 'bg-red-500/70' : 'bg-blue-600/60'}`}
                    style={{ height: `${Math.max(pct, 3)}%` }}
                    title={`${m}: ${formatEUR(val)}`} />
                  <span className="text-[8px] text-muted-foreground font-mono">{m.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <div className="relative mt-1" style={{ height: '2px' }}>
            <div className="absolute left-0 right-0 border-t border-dashed border-amber-500/50" style={{ top: 0 }} />
          </div>
          <p className="text-[9px] text-amber-500/60 text-right mt-0.5">Monthly pace: {formatEUR(monthly_budget)}</p>
        </div>
      )}

      {/* Cost Items */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="font-heading text-base text-foreground">Cost Items ({cost_items.length})</p>
        </div>

        {cost_items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No cost items yet. Click "Add Cost Item" to get started.</p>
        ) : (
          <div>
            <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] text-foreground/40 uppercase tracking-wider border-b border-border bg-secondary/30">
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2 text-center">Status</div>
              <div className="col-span-1 text-center">Docs</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {cost_items.map(item => {
              const isPending = item.approval_status === 'pending';
              const isRejected = item.approval_status === 'rejected';
              const isApproved = item.approval_status === 'approved' || !item.approval_status;
              const createdByMe = item.created_by === user?.id;
              const canApprove = isPending && !createdByMe;

              let rowBg = '';
              if (isPending) rowBg = 'bg-amber-950/5 border-l-2 border-l-amber-500/30';
              else if (isRejected) rowBg = 'bg-red-950/5 border-l-2 border-l-red-500/20 opacity-60';

              return (
              <div key={item.id} className={`grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-border hover:bg-secondary/10 items-center ${rowBg}`} data-testid={`cost-item-${item.id}`}>
                <div className="col-span-2 text-xs text-muted-foreground font-mono">{item.date}</div>
                <div className="col-span-3">
                  <p className="text-sm text-foreground truncate">{item.description || '—'}</p>
                  {item.recurring && <p className="text-[10px] text-blue-400">Recurring: {formatEUR(item.recurring_amount)}/mo</p>}
                  {item.notes && <p className="text-[10px] text-muted-foreground truncate">{item.notes}</p>}
                  <p className="text-[10px] text-muted-foreground/50">{item.created_by_name || 'Unknown'}</p>
                </div>
                <div className={`col-span-2 text-right font-mono text-sm ${isRejected ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{formatEUR(item.amount)}</div>
                <div className="col-span-2 flex items-center justify-center">
                  {isPending && (
                    <span className="flex items-center gap-1 text-[10px] text-amber-400 border border-amber-800/30 bg-amber-900/10 rounded px-1.5 py-0.5 font-mono">
                      <Clock className="w-3 h-3" /> PENDING
                    </span>
                  )}
                  {isApproved && item.approval_status === 'approved' && item.approved_by && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400 border border-emerald-800/30 bg-emerald-900/10 rounded px-1.5 py-0.5 font-mono">
                      <CheckCircle className="w-3 h-3" /> APPROVED
                    </span>
                  )}
                  {isRejected && (
                    <span className="flex items-center gap-1 text-[10px] text-red-400 border border-red-800/30 bg-red-900/10 rounded px-1.5 py-0.5 font-mono">
                      <XCircle className="w-3 h-3" /> REJECTED
                    </span>
                  )}
                </div>
                <div className="col-span-1 flex items-center justify-center gap-1">
                  {(item.documents || []).map(doc => (
                    <span key={doc.id} className="relative group/doc inline-flex" data-testid={`cost-doc-${doc.id}`}>
                      <a href={`${API_BASE}/api/budget/cost-items/${item.id}/documents/${doc.id}/file`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 transition-colors" title={doc.filename}>
                        <FileText className="w-3.5 h-3.5" />
                      </a>
                      {(createdByMe || user?.role === 'admin') && (
                        <button onClick={() => handleDocDelete(item, doc)} data-testid={`cost-doc-delete-${doc.id}`}
                          title={`Beleg löschen: ${doc.filename}`}
                          className="absolute -top-2 -right-2 w-3.5 h-3.5 rounded-full bg-red-600 text-white items-center justify-center hidden group-hover/doc:inline-flex focus:inline-flex">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </span>
                  ))}
                  <label className="cursor-pointer text-muted-foreground/40 hover:text-foreground transition-colors" title="Attach document">
                    <input type="file" className="hidden" onChange={e => e.target.files[0] && handleDocUpload(item.id, e.target.files[0])} />
                    {uploading === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                  </label>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  {canApprove && (
                    <>
                      <button onClick={() => handleApprove(item.id)} className="text-emerald-500/60 hover:text-emerald-400 transition-colors" title="Approve" data-testid={`approve-${item.id}`}>
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleReject(item.id)} className="text-red-500/40 hover:text-red-400 transition-colors" title="Reject" data-testid={`reject-${item.id}`}>
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {isPending && createdByMe && (
                    <span className="text-[9px] text-muted-foreground/50 italic">Awaiting approval</span>
                  )}
                  <button onClick={() => handleDelete(item.id)} className="text-muted-foreground/30 hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Cost Item Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" /> Add Cost Item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Description</p>
              <Input value={newDesc} onChange={e => setNewDesc(e.target.value)} placeholder="e.g. Monthly rent payment"
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground" data-testid="cost-item-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">{includeVat ? 'Gross Amount (EUR)' : 'Amount (EUR)'}</p>
                <Input type="number" step="0.01" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="0"
                  className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground font-mono" data-testid="cost-item-amount" />
              </div>
              <div>
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Date</p>
                <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                  min="2026-06-01"
                  className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground font-mono" data-testid="cost-item-date" />
              </div>
            </div>

            {/* VAT Split Toggle */}
            {!isVatRubrique && (
            <div className="border border-border rounded-sm p-3 bg-secondary/20">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeVat} onChange={e => setIncludeVat(e.target.checked)}
                  className="accent-blue-500" data-testid="vat-split-toggle" />
                <span className="text-xs text-foreground font-medium">Split VAT automatically</span>
              </label>
              {includeVat && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-[10px] text-foreground/40 uppercase tracking-wider">VAT Rate (%)</p>
                    <Input type="number" step="1" value={vatRate} onChange={e => setVatRate(e.target.value)}
                      className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground font-mono w-20" data-testid="vat-rate-input" />
                  </div>
                  {newAmount && parseFloat(newAmount) > 0 && (() => {
                    const gross = parseFloat(newAmount);
                    const rate = parseFloat(vatRate || '19') / 100;
                    const net = Math.round(gross / (1 + rate) * 100) / 100;
                    const vat = Math.round((gross - net) * 100) / 100;
                    return (
                      <div className="grid grid-cols-3 gap-2 text-center bg-card border border-border/50 rounded-sm p-2">
                        <div>
                          <p className="text-[9px] text-emerald-400/60 uppercase">Net → Budget</p>
                          <p className="text-sm font-mono font-semibold text-emerald-400">{formatEUR(net)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-blue-400/60 uppercase">VAT ({vatRate}%)</p>
                          <p className="text-sm font-mono text-blue-400">{formatEUR(vat)}</p>
                        </div>
                        <div>
                          <p className="text-[9px] text-foreground/40 uppercase">Gross (entered)</p>
                          <p className="text-sm font-mono text-foreground/60">{formatEUR(gross)}</p>
                        </div>
                      </div>
                    );
                  })()}
                  <p className="text-[9px] text-muted-foreground">
                    Enter the total invoice amount (gross). Net goes to this category, VAT to "VAT (19%) — Receivable".
                  </p>
                </div>
              )}
            </div>
            )}

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newRecurring} onChange={e => setNewRecurring(e.target.checked)} className="accent-blue-500" />
                <span className="text-xs text-foreground">This is a recurring monthly cost</span>
              </label>
              {newRecurring && (
                <div className="mt-2">
                  <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Monthly Amount (EUR)</p>
                  <Input type="number" step="0.01" value={newRecurringAmount} onChange={e => setNewRecurringAmount(e.target.value)} placeholder="e.g. 1667"
                    className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground font-mono" />
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Notes</p>
              <Input value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Optional notes"
                className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleAdd} disabled={saving || !newAmount}
                data-testid="save-cost-item-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-4">
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Saving...</> : (includeVat ? 'Add Net + VAT' : 'Add Cost Item')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
